/**
 * Voice typing with the browser's Web Speech API: the browser turns speech into text and we only ever handle that text —
 * no audio is recorded, uploaded or stored by Hearth. This module is framework-free: the recognizer lifecycle
 * (`Dictation`) and the pure formatting that decides how dictated words land in a text box. React glue:
 * `hooks/useDictation`, `components/ui/VoiceTextarea`.
 */

// lib.dom declares the result types but not the recognizer itself, which Chrome and Safari still ship prefixed.
export type DictationResult = { readonly isFinal: boolean; readonly [alternative: number]: { readonly transcript: string } };
export type DictationResultEvent = { readonly resultIndex: number; readonly results: ArrayLike<DictationResult> };
export type DictationErrorEvent = { readonly error: string };

export type SpeechRecognizer = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: DictationResultEvent) => void) | null;
  onerror: ((event: DictationErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
};

export type SpeechRecognizerConstructor = new () => SpeechRecognizer;

type SpeechWindow = { SpeechRecognition?: SpeechRecognizerConstructor; webkitSpeechRecognition?: SpeechRecognizerConstructor };

/** The browser's speech recognizer, or null (Firefox, some in-app browsers). */
export function speechRecognizer(): SpeechRecognizerConstructor | null {
  if (typeof window === 'undefined') return null;
  const speech = window as unknown as SpeechWindow;
  return speech.SpeechRecognition ?? speech.webkitSpeechRecognition ?? null;
}

// ---------------------------------------------------------------------------------------------------------------------
// Language

export const DICTATION_LANGUAGES = [
  { value: 'en-US', label: 'English' },
  { value: 'fr-CA', label: 'Français' },
] as const;
export type DictationLang = (typeof DICTATION_LANGUAGES)[number]['value'];

const LANG_KEY = 'hearth.dictationLang';

function isDictationLang(value: string | null): value is DictationLang {
  return DICTATION_LANGUAGES.some((lang) => lang.value === value);
}

/** The language picked last time, else the browser's (French → fr-CA, anything else → en-US). */
export function preferredDictationLang(): DictationLang {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (isDictationLang(saved)) return saved;
  } catch {
    // Storage disabled (privacy mode): fall back to the browser language.
  }
  return typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('fr') ? 'fr-CA' : 'en-US';
}

export function saveDictationLang(lang: DictationLang): void {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    // Not remembered, but still used for this page.
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Problems

export type DictationProblem = 'unsupported' | 'unavailable' | 'blocked' | 'no-microphone' | 'network' | 'language' | 'paused' | 'failed';

const KEYBOARD_MIC = 'Voice typing isn’t available here — use your keyboard’s microphone button instead.';

/** Plain-words explanation shown under the box. */
export function dictationProblemMessage(problem: DictationProblem, { touch = false } = {}): string {
  switch (problem) {
    case 'unsupported':
      // Phone keyboards have their own dictation; desktop keyboards don't, so point to a browser that can do it.
      return touch ? KEYBOARD_MIC : 'Voice typing isn’t available in this browser — try Chrome, Edge or Safari.';
    case 'unavailable':
      return KEYBOARD_MIC;
    case 'blocked':
      return 'Microphone access is blocked — allow it in your browser’s site settings.';
    case 'no-microphone':
      return 'No microphone found — check that one is connected and not in use by another app.';
    case 'network':
      return 'Voice typing needs an internet connection — check it and try again.';
    case 'language':
      return 'Voice typing doesn’t support this language here — try the other one.';
    case 'paused':
      return 'Voice typing paused — press the microphone to keep going.';
    case 'failed':
      return 'Voice typing stopped unexpectedly — press the microphone to try again.';
  }
}

/**
 * Maps a SpeechRecognition error code to what we tell the user; null = not a problem (silence, or our own abort).
 * `restarted`: the session was one we restarted automatically — a refusal then means the browser wants a new tap, not
 * that the microphone is blocked (Safari only allows starting from a user gesture).
 */
export function problemForError(code: string, restarted: boolean): DictationProblem | null {
  switch (code) {
    case 'no-speech':
    case 'aborted':
      return null;
    case 'not-allowed':
      return restarted ? 'paused' : 'blocked';
    case 'service-not-allowed':
      return 'unavailable';
    case 'audio-capture':
      return 'no-microphone';
    case 'network':
      return 'network';
    case 'language-not-supported':
      return 'language';
    default:
      return 'failed';
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Formatting

const PARAGRAPH_COMMAND = /[^\S\n]*\b(?:new paragraph|nouveau paragraphe)\b[^\S\n]*/gi;
const LINE_COMMAND = /[^\S\n]*\b(?:new ?line|nouvelle ligne)\b[^\S\n]*/gi;
const SENTENCE_END = /[.!?…]$/;

/**
 * Tidies a final transcript for the spot it lands after (`before` = the text before the cursor): spoken "new line" /
 * "new paragraph" (and "nouvelle ligne" / "nouveau paragraphe") become line breaks, spaces collapse, and a letter that
 * starts the box, a line or a sentence is capitalized. It adds no punctuation of its own.
 */
export function formatDictation(transcript: string, before = ''): string {
  let text = transcript
    .replace(PARAGRAPH_COMMAND, '\n\n')
    .replace(LINE_COMMAND, '\n')
    .replace(/[^\S\n]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .replace(/^ | $/g, '');
  if (text === '') return '';

  const tail = before.replace(/[^\S\n]+$/, '');
  if (tail === '' || tail.endsWith('\n') || SENTENCE_END.test(tail)) text = text.replace(/^\p{Ll}/u, (letter) => letter.toUpperCase());
  return text.replace(/([.!?…] |\n)(\p{Ll})/gu, (_, separator: string, letter: string) => separator + letter.toUpperCase());
}

/** Whether two pieces of text need a space between them (none next to existing whitespace or before punctuation). */
function needsSpace(left: string, right: string): boolean {
  return left !== '' && right !== '' && !/[\s(]$/.test(left) && !/^[\s.,!?;:…)]/.test(right);
}

export type DictationEdit = { value: string; caret: number };

/**
 * Puts dictated words into `value` at the selection (replacing any selected text), formatted and spaced for where they
 * land. `caret` is where the cursor goes: right after the new words, so the next phrase follows them.
 */
export function insertDictation(value: string, transcript: string, start = value.length, end = start): DictationEdit {
  let before = value.slice(0, start);
  const after = value.slice(end);
  const words = formatDictation(transcript, before);
  if (words === '') return { value, caret: end };
  // A spoken line break replaces trailing spaces rather than leaving them dangling at the end of the line.
  if (words.startsWith('\n')) before = before.replace(/[^\S\n]+$/, '');
  const inserted = (needsSpace(before, words) ? ' ' : '') + words;
  const trailing = needsSpace(words, after) ? ' ' : '';
  return { value: before + inserted + trailing + after, caret: before.length + inserted.length };
}

// ---------------------------------------------------------------------------------------------------------------------
// Recognizer lifecycle

export type DictationEvents = {
  /** Final words, unformatted — pass them to `insertDictation`. */
  onText: (text: string) => void;
  /** Words still being recognised (may change); '' when there are none. */
  onInterim: (text: string) => void;
  onListening: (listening: boolean) => void;
  onProblem: (problem: DictationProblem | null) => void;
};

/** A session that ends sooner than this after starting counts as a failed start. */
const QUICK_END_MS = 1000;
/** Consecutive failed starts before giving up instead of restarting in a loop. */
const MAX_QUICK_ENDS = 3;

/**
 * Keeps voice typing going until the user stops it. Browsers end a recognition session on their own after a pause
 * (Chrome, Safari) or some silence, so while the user wants to listen, every ended session is replaced by a new one.
 * Stopping keeps the words that were still being recognised instead of dropping them.
 */
export class Dictation {
  readonly #Recognizer: SpeechRecognizerConstructor;
  readonly #events: DictationEvents;
  #lang: string;
  #recognizer: SpeechRecognizer | null = null;
  #wanted = false;
  #interim = '';
  #quickEnds = 0;

  constructor(Recognizer: SpeechRecognizerConstructor, events: DictationEvents, lang: string) {
    this.#Recognizer = Recognizer;
    this.#events = events;
    this.#lang = lang;
  }

  get listening(): boolean {
    return this.#wanted;
  }

  start(): void {
    if (this.#wanted) return;
    this.#wanted = true;
    this.#quickEnds = 0;
    this.#events.onProblem(null);
    this.#events.onListening(true);
    this.#startSession(false);
  }

  /** Stops listening; words still being recognised are delivered as final text first. */
  stop(): void {
    if (!this.#wanted) return;
    this.#wanted = false;
    this.#flushInterim();
    this.#abort();
    this.#events.onListening(false);
  }

  /** Stops without delivering anything more (the text box is going away). Safe to start again afterwards. */
  dispose(): void {
    this.#wanted = false;
    this.#interim = '';
    this.#abort();
  }

  /** Switches language; a running session restarts in the new one. */
  setLang(lang: string): void {
    if (lang === this.#lang) return;
    this.#lang = lang;
    if (!this.#wanted) return;
    this.#flushInterim();
    this.#abort();
    this.#startSession(true);
  }

  #startSession(restarted: boolean): void {
    const recognizer = new this.#Recognizer();
    recognizer.lang = this.#lang;
    recognizer.continuous = true;
    recognizer.interimResults = true;
    const startedAt = Date.now();
    // Handlers ignore a recognizer we have already replaced or aborted.
    recognizer.onresult = (event) => {
      if (this.#recognizer === recognizer) this.#onResult(event);
    };
    recognizer.onerror = (event) => {
      if (this.#recognizer !== recognizer) return;
      const problem = problemForError(event.error, restarted);
      if (problem) this.#fail(problem);
    };
    recognizer.onend = () => {
      if (this.#recognizer === recognizer) this.#onEnd(Date.now() - startedAt);
    };
    this.#recognizer = recognizer;
    try {
      recognizer.start();
    } catch {
      this.#fail('failed');
    }
  }

  #onResult(event: DictationResultEvent): void {
    let final = '';
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const result = event.results[i];
      const transcript = result?.[0]?.transcript ?? '';
      if (result?.isFinal) final += transcript;
      else interim += transcript;
    }
    if (final.trim() !== '') this.#events.onText(final);
    this.#setInterim(interim);
  }

  #onEnd(elapsed: number): void {
    this.#recognizer = null;
    this.#flushInterim(); // A session can end before its last words were finalised.
    if (!this.#wanted) return;
    this.#quickEnds = elapsed < QUICK_END_MS ? this.#quickEnds + 1 : 0;
    if (this.#quickEnds >= MAX_QUICK_ENDS) {
      this.#fail('failed');
      return;
    }
    this.#startSession(true);
  }

  #fail(problem: DictationProblem): void {
    this.#wanted = false;
    this.#flushInterim();
    this.#abort();
    this.#events.onListening(false);
    this.#events.onProblem(problem);
  }

  #flushInterim(): void {
    const pending = this.#interim;
    this.#setInterim('');
    if (pending.trim() !== '') this.#events.onText(pending);
  }

  #setInterim(text: string): void {
    if (text === this.#interim) return;
    this.#interim = text;
    this.#events.onInterim(text);
  }

  #abort(): void {
    const recognizer = this.#recognizer;
    this.#recognizer = null;
    if (!recognizer) return;
    recognizer.onresult = null;
    recognizer.onerror = null;
    recognizer.onend = null;
    try {
      recognizer.abort();
    } catch {
      // Already ended.
    }
  }
}
